import React, { useContext, useState, useEffect } from 'react';
import {
  Route,
  Switch,
  useRouteMatch,
  NavLink as RRNavLink,
  useHistory,
} from 'react-router-dom';
import * as ROUTES from '../../constants/routes';
import { useAuthStore } from '../../store/auth.store';
import { Col, Row, Container, Nav, NavItem, NavLink } from 'reactstrap';
import Members from '../../pages/Admin/Members';
import Invites from '../../pages/Admin/Invites';
import Users from '../../pages/Admin/Users';
import Fields from '../../pages/Admin/Fields';
import ChatbotSettings from '../../pages/Admin/Chatbot'; //chatbot feature
import Analytics from '../../pages/Admin/Analytics'; // Analytics page (to be created)
import Reporting from '../../pages/Admin/Reporting'; // Reporting page

import { withAuthorization } from '../../components/Session';
import { useOrganizationStore } from '../../store/organization.store';
import { FirebaseContext } from '../../components/Firebase';
import ModalConsumer from '../../components/utility/Modal/ModalConsumer';
import GenericConfirmModal from '../../components/GenericModal/ConfirmModal';
import { useToast } from '../../contexts/ToastContext';
import {
  DEFAULT_EXPIRY_LEAD_TIME_DAYS,
  MIN_EXPIRY_LEAD_TIME_DAYS,
  MAX_EXPIRY_LEAD_TIME_DAYS,
} from '../../services/notificationPreferences';

const AdminPage = () => {
  let { path } = useRouteMatch();
  const history = useHistory();
  const currentOrgRole = useOrganizationStore((state) => state.currentOrgRole);
  const currentOrgId = useOrganizationStore((state) => state.currentOrgId);
  const currentOrgName = useOrganizationStore((state) => state.currentOrgName);
  const currentOrgType = useOrganizationStore((state) => state.currentOrgType);
  const currentOrgCategory = useOrganizationStore((state) => state.currentOrgCategory);
  const setCurrentOrganization = useOrganizationStore((state) => state.setCurrentOrganization);
  const setOrganizations = useOrganizationStore((state) => state.setOrganizations);
  const isSuperAdmin = useOrganizationStore((state) => state.isSuperAdmin);
  const auth = useAuthStore((state) => state.auth);
  const firebase = useContext(FirebaseContext);
  const { showSuccess, showError } = useToast();
  const [orphanedFolders, setOrphanedFolders] = useState(null);
  const [loadingOrphans, setLoadingOrphans] = useState(false);
  const [orphanError, setOrphanError] = useState(null);
  const [deletingFolder, setDeletingFolder] = useState('');
  const [deletingAll, setDeletingAll] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, type: '', folder: null });

  // Organization settings state
  const [editingOrgName, setEditingOrgName] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [savingOrgName, setSavingOrgName] = useState(false);
  const [orgSaveError, setOrgSaveError] = useState(null);
  const [orgSaveSuccess, setOrgSaveSuccess] = useState(false);

  // Delete org state
  const [showDeleteOrgConfirm, setShowDeleteOrgConfirm] = useState(false);
  const [deleteOrgConfirmText, setDeleteOrgConfirmText] = useState('');
  const [isDeletingOrg, setIsDeletingOrg] = useState(false);
  const [deleteOrgError, setDeleteOrgError] = useState(null);

  // Notification settings state (org-level license expiry lead time)
  const [leadTimeDays, setLeadTimeDays] = useState(DEFAULT_EXPIRY_LEAD_TIME_DAYS);
  const [leadTimeLoaded, setLeadTimeLoaded] = useState(false);
  const [savingLeadTime, setSavingLeadTime] = useState(false);
  const [leadTimeSaveError, setLeadTimeSaveError] = useState(null);
  const [leadTimeSaveSuccess, setLeadTimeSaveSuccess] = useState(false);

  // Load the current org's lead-time setting when the org changes.
  useEffect(() => {
    setLeadTimeLoaded(false);
    setLeadTimeSaveError(null);
    setLeadTimeSaveSuccess(false);

    if (!firebase || !currentOrgId) return;

    (async () => {
      try {
        const snap = await firebase.getOrganization(currentOrgId);
        if (snap.exists()) {
          const stored = snap.data().licenseExpiryLeadTimeDays;
          setLeadTimeDays(
            typeof stored === 'number' && stored > 0
              ? stored
              : DEFAULT_EXPIRY_LEAD_TIME_DAYS
          );
        }
        setLeadTimeLoaded(true);
      } catch (err) {
        console.error('Error loading org lead-time setting:', err);
        setLeadTimeLoaded(true);
      }
    })();
  }, [firebase, currentOrgId]);

  const handleSaveLeadTime = async () => {
    if (!firebase || !currentOrgId) return;
    const clamped = Math.min(
      MAX_EXPIRY_LEAD_TIME_DAYS,
      Math.max(MIN_EXPIRY_LEAD_TIME_DAYS, Math.round(leadTimeDays))
    );
    setSavingLeadTime(true);
    setLeadTimeSaveError(null);
    setLeadTimeSaveSuccess(false);
    try {
      await firebase.updateOrganization(currentOrgId, {
        licenseExpiryLeadTimeDays: clamped,
      });
      setLeadTimeDays(clamped);
      setLeadTimeSaveSuccess(true);
      setTimeout(() => setLeadTimeSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving lead-time setting:', err);
      setLeadTimeSaveError(err.message || 'Failed to save setting');
    } finally {
      setSavingLeadTime(false);
    }
  };

  // Check org-based admin role
  if (!isSuperAdmin && currentOrgRole !== 'ADMIN'){
    return null;
  }

  const handleFindOrphanedFolders = async () => {
    setLoadingOrphans(true);
    setOrphanError(null);
    setOrphanedFolders(null);
    try {
      // Scan all organizations for orphaned folders
      const result = await firebase.callFunction('findOrphanedAudioFolders', {});
      setOrphanedFolders(result.data.orphaned);
    } catch (err) {
      setOrphanError(err.message || 'Error fetching orphaned folders');
    } finally {
      setLoadingOrphans(false);
    }
  };

  const handleDeleteFolder = async (folder, orgId) => {
    setConfirmModal({ open: true, type: 'single', folder, orgId });
  };

  const handleDeleteAllFolders = async () => {
    setConfirmModal({ open: true, type: 'all', folder: null, orgId: null });
  };

  const confirmDeleteFolder = async (folder, orgId) => {
    setDeletingFolder(folder);
    setOrphanError(null);
    try {
      // Pass orgId from the orphaned item to delete from correct storage location
      await firebase.callFunction('deleteAudioFolder', { folder, orgId });
      await handleFindOrphanedFolders();
    } catch (err) {
      setOrphanError(err.message || `Error deleting folder ${folder}`);
    } finally {
      setDeletingFolder('');
      setConfirmModal({ open: false, type: '', folder: null, orgId: null });
    }
  };

  const confirmDeleteAllFolders = async () => {
    setDeletingAll(true);
    setOrphanError(null);
    try {
      for (const { folder, orgId } of orphanedFolders) {
        // Pass orgId from each orphaned item
        await firebase.callFunction('deleteAudioFolder', { folder, orgId });
      }
      await handleFindOrphanedFolders();
    } catch (err) {
      setOrphanError(err.message || 'Error deleting all orphaned folders');
    } finally {
      setDeletingAll(false);
      setConfirmModal({ open: false, type: '', folder: null, orgId: null });
    }
  };

  // Group orphaned folders by organization for display
  const groupedOrphans = orphanedFolders ? orphanedFolders.reduce((acc, item) => {
    const key = item.orgName || 'Unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {}) : {};

  // Organization name edit handlers
  const handleStartEditOrgName = () => {
    setNewOrgName(currentOrgName || '');
    setEditingOrgName(true);
    setOrgSaveError(null);
    setOrgSaveSuccess(false);
  };

  const handleCancelEditOrgName = () => {
    setEditingOrgName(false);
    setNewOrgName('');
    setOrgSaveError(null);
  };

  const handleSaveOrgName = async () => {
    if (!newOrgName.trim()) {
      setOrgSaveError('Organization name cannot be empty');
      return;
    }
    if (newOrgName.trim() === currentOrgName) {
      setEditingOrgName(false);
      return;
    }

    setSavingOrgName(true);
    setOrgSaveError(null);
    try {
      // Update org name in the organization doc and all members' denormalized copies
      await firebase.updateOrganizationName(currentOrgId, newOrgName.trim());
      // Update the local store with the new name (preserve type and category)
      setCurrentOrganization(currentOrgId, newOrgName.trim(), currentOrgRole, currentOrgType, currentOrgCategory);
      setOrgSaveSuccess(true);
      setEditingOrgName(false);
      setTimeout(() => setOrgSaveSuccess(false), 3000);
    } catch (err) {
      setOrgSaveError(err.message || 'Failed to update organization name');
    } finally {
      setSavingOrgName(false);
    }
  };

  // Handle soft delete organization
  const handleDeleteOrganization = async () => {
    if (!firebase || !auth || !currentOrgId || !currentOrgName) return;

    // Verify confirmation text matches org name
    if (deleteOrgConfirmText !== currentOrgName) {
      setDeleteOrgError('Organization name does not match. Please type the exact name to confirm deletion.');
      return;
    }

    setIsDeletingOrg(true);
    setDeleteOrgError(null);

    const deletedOrgName = currentOrgName;

    try {
      await firebase.softDeleteOrganization(currentOrgId, auth.uid);

      // Refresh organizations from Firestore to get the latest state
      const updatedOrgs = await firebase.getUserOrganizations(auth.uid);
      setOrganizations(updatedOrgs);

      showSuccess(`Organization "${deletedOrgName}" has been deleted`);

      // Switch to another org if available, otherwise redirect to get-started
      if (updatedOrgs.length > 0) {
        const firstOrg = updatedOrgs[0];
        setCurrentOrganization(firstOrg.orgId, firstOrg.orgName, firstOrg.role, firstOrg.orgType || 'artist', firstOrg.orgCategory);
        history.push(ROUTES.FIND_AUDIO);
      } else {
        // No orgs left - redirect to get-started
        history.push(ROUTES.GET_STARTED);
      }
    } catch (err) {
      console.error('Error deleting organization:', err);
      setDeleteOrgError(err.message || 'Failed to delete organization');
      showError(`Failed to delete organization: ${err.message}`);
    } finally {
      setIsDeletingOrg(false);
    }
  };

  // Organization Settings Card UI
  const OrganizationCard = (
    <div className="mt-4 mb-4 p-4" style={{ background: 'rgba(255, 255, 255, 0.07)', borderRadius: 8 }}>
      <h4>Organization Settings</h4>
      <div className="mt-4">
        <label className="text-muted d-block mb-2">Organization Name</label>
        {!editingOrgName ? (
          <div className="d-flex align-items-center">
            <span style={{ fontSize: '1.25rem', fontWeight: 500 }}>{currentOrgName}</span>
            <button
              className="btn btn-sm btn-warning-custom btn-round ml-3"
              onClick={handleStartEditOrgName}
            >
              <span>Edit</span>
            </button>
          </div>
        ) : (
          <div className="d-flex align-items-center">
            <input
              type="text"
              className="form-control"
              style={{ maxWidth: 300 }}
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              placeholder="Enter organization name"
              disabled={savingOrgName}
            />
            <button
              className="btn btn-sm btn-success btn-round ml-2"
              onClick={handleSaveOrgName}
              disabled={savingOrgName}
            >
              <span>{savingOrgName ? 'Saving...' : 'Save'}</span>
            </button>
            <button
              className="btn btn-sm btn-secondary btn-round ml-2"
              onClick={handleCancelEditOrgName}
              disabled={savingOrgName}
            >
              <span>Cancel</span>
            </button>
          </div>
        )}
        {orgSaveError && <div className="text-danger mt-2">{orgSaveError}</div>}
        {orgSaveSuccess && <div className="text-success mt-2">Organization name updated successfully!</div>}
      </div>
      <div className="mt-4">
        <label className="text-muted d-block mb-2">Organization ID</label>
        <code style={{ fontSize: '0.9rem', color: '#888' }}>{currentOrgId}</code>
      </div>

      {/* Notification Settings — org-level */}
      <div className="mt-5 pt-4" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <h5 className="mb-3">Notification Settings</h5>
        <p className="text-muted mb-3">
          These settings affect notifications sent to all members of this organization.
        </p>
        <label className="text-muted d-block mb-2">
          License expiry lead time (days)
        </label>
        <div className="d-flex align-items-center">
          <input
            type="number"
            className="form-control"
            style={{ maxWidth: 160 }}
            min={MIN_EXPIRY_LEAD_TIME_DAYS}
            max={MAX_EXPIRY_LEAD_TIME_DAYS}
            step={1}
            value={leadTimeDays}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              setLeadTimeDays(Number.isFinite(n) ? n : DEFAULT_EXPIRY_LEAD_TIME_DAYS);
            }}
            disabled={!leadTimeLoaded || savingLeadTime}
          />
          <button
            className="btn btn-sm btn-success btn-round ml-3"
            onClick={handleSaveLeadTime}
            disabled={!leadTimeLoaded || savingLeadTime}
          >
            <span>{savingLeadTime ? 'Saving...' : 'Save'}</span>
          </button>
        </div>
        <small className="text-muted d-block mt-2">
          How many days before a license's expiry date we'll send a heads-up
          notification — to both your organization and the other party on the
          license. Default is {DEFAULT_EXPIRY_LEAD_TIME_DAYS} days. Allowed range:{' '}
          {MIN_EXPIRY_LEAD_TIME_DAYS}–{MAX_EXPIRY_LEAD_TIME_DAYS}.
        </small>
        {leadTimeSaveError && <div className="text-danger mt-2">{leadTimeSaveError}</div>}
        {leadTimeSaveSuccess && <div className="text-success mt-2">Settings saved.</div>}
      </div>

      {/* Danger Zone - Delete Organization */}
      <div className="mt-5 pt-4" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <h5 className="text-danger mb-3">Danger Zone</h5>
        {!showDeleteOrgConfirm ? (
          <div>
            <p className="text-muted mb-3">
              Deleting an organization will remove it from all members and archive all data. This action can be reversed by a super admin.
            </p>
            <button
              className="btn btn-danger btn-round"
              onClick={() => setShowDeleteOrgConfirm(true)}
            >
              <span>Delete Organization</span>
            </button>
          </div>
        ) : (
          <div style={{ background: 'rgba(255, 0, 0, 0.1)', padding: 16, borderRadius: 8, border: '1px solid rgba(255, 0, 0, 0.3)' }}>
            <p className="text-danger mb-2">
              <strong>Warning:</strong> This will delete the organization "{currentOrgName}" and remove it from all members.
            </p>
            <p className="text-muted mb-3">
              To confirm, type the organization name: <strong>{currentOrgName}</strong>
            </p>
            <input
              type="text"
              className="form-control mb-3"
              style={{ maxWidth: 300 }}
              value={deleteOrgConfirmText}
              onChange={(e) => setDeleteOrgConfirmText(e.target.value)}
              placeholder="Type organization name to confirm"
              disabled={isDeletingOrg}
            />
            {deleteOrgError && <div className="text-danger mb-3">{deleteOrgError}</div>}
            <div className="d-flex">
              <button
                className="btn btn-danger btn-round mr-2"
                onClick={handleDeleteOrganization}
                disabled={isDeletingOrg || deleteOrgConfirmText !== currentOrgName}
              >
                <span>{isDeletingOrg ? 'Deleting...' : 'Confirm Delete'}</span>
              </button>
              <button
                className="btn btn-secondary btn-round"
                onClick={() => {
                  setShowDeleteOrgConfirm(false);
                  setDeleteOrgConfirmText('');
                  setDeleteOrgError(null);
                }}
                disabled={isDeletingOrg}
              >
                <span>Cancel</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Cleanup Card UI
  const CleanupCard = (
    <ModalConsumer>
      {({ showModal }) => (
        <div className="mt-4 mb-4 p-3" style={{ background: 'rgba(255, 255, 255, 0.07)', borderRadius: 8 }}>
          <h4>Orphaned Audio Folders</h4>
          <p className="text-muted mb-3">
            Scans all organizations for orphaned audio files in storage that have no corresponding track metadata.
          </p>
          <button className="btn btn-info-custom btn-round mb-3" onClick={handleFindOrphanedFolders} disabled={loadingOrphans}>
            <span>{loadingOrphans ? 'Searching all organizations...' : 'Find Orphaned Audio Folders'}</span>
          </button>
          {orphanedFolders && orphanedFolders.length > 0 && (
            <button
              className="btn btn-danger-custom btn-round mb-3 ml-2"
              onClick={() => handleDeleteAllFolders()}
              disabled={deletingAll}
            >
              <span>{deletingAll ? 'Deleting All...' : `Delete All (${orphanedFolders.length}) Orphaned Folders`}</span>
            </button>
          )}
          {orphanError && <div className="text-danger mb-2">{orphanError}</div>}
          {orphanedFolders && (
            orphanedFolders.length === 0 ? (
              <div className="text-success">No orphaned folders found across all organizations.</div>
            ) : (
              <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                {Object.entries(groupedOrphans).map(([orgName, items]) => (
                  <div key={orgName} style={{ marginBottom: 24 }}>
                    <h5 style={{ color: '#1d8cf8', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: 8, marginBottom: 12 }}>
                      {orgName} <span style={{ fontSize: 14, color: '#888' }}>({items.length} orphaned)</span>
                    </h5>
                    <ul style={{ listStyle: 'none', paddingLeft: 0, margin: 0 }}>
                      {items.map(({ folder, files, orgId }) => (
                        <li
                          key={`${orgId || 'legacy'}-${folder}`}
                          style={{
                            marginBottom: 12,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: 'var(--chatbot-color-2, rgba(255,255,255,0.02))',
                            borderRadius: 6,
                            padding: '8px 12px',
                          }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                            <strong style={{ color: '#fd5d93', marginBottom: 4 }}>{folder}</strong>
                            <ul style={{ marginLeft: 0, paddingLeft: 16, marginBottom: 0 }}>
                              {files.map(f => (
                                <li key={f} style={{ color: '#fff', wordBreak: 'break-all', padding: 0, margin: 0, fontSize: 13 }}>{f}</li>
                              ))}
                            </ul>
                          </div>
                          <button
                            className="btn btn-danger-custom btn-sm ml-2"
                            style={{ minWidth: 90, alignSelf: 'center' }}
                            disabled={deletingFolder === folder}
                            onClick={() => handleDeleteFolder(folder, orgId)}
                          >
                            <span>{deletingFolder === folder ? 'Deleting...' : 'Delete'}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )
          )}
          {confirmModal.open && (
            <GenericConfirmModal
              isOpen={true}
              title={confirmModal.type === 'all' ? 'Delete All Orphaned Folders' : `Delete Folder "${confirmModal.folder}"`}
              body={confirmModal.type === 'all'
                ? `Are you sure you want to delete ALL ${orphanedFolders.length} orphaned folders across all organizations? This cannot be undone.`
                : `Are you sure you want to delete all files in folder "${confirmModal.folder}"? This cannot be undone.`}
              callback={() => {
                if (confirmModal.type === 'all') confirmDeleteAllFolders();
                else confirmDeleteFolder(confirmModal.folder, confirmModal.orgId);
              }}
              cancelCallback={() => setConfirmModal({ open: false, type: '', folder: null, orgId: null })}
            />
          )}
        </div>
      )}
    </ModalConsumer>
  );

  return (
    <div className="wrapper pt-3 pb-3">
      <Container fluid style={{ paddingLeft: '100px', paddingRight: '100px' }}>
        <Row>
          <Col md="2" lg="2" className="pr-3">
            <div className="mb-4">
              <h4 className="d-flex flex-row align-items-center mb-2">
                <i className="tim-icons icon-lock-circle accent-1-icon mr-2"></i>
                Setup
              </h4>
            </div>

            <Nav vertical className="nav-pills">
              <NavItem>
                <NavLink tag={RRNavLink} exact activeClassName="active" to={ROUTES.ADMIN}>
                  <i className="tim-icons icon-badge mr-2"></i>
                  Members
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink tag={RRNavLink} activeClassName="active" to={ROUTES.ADMIN + '/invites'}>
                  <i className="tim-icons icon-email-85 mr-2"></i>
                  Invites
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink tag={RRNavLink} activeClassName="active" to={ROUTES.ADMIN + '/organization'}>
                  <i className="tim-icons icon-bank mr-2"></i>
                  Organization
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink tag={RRNavLink} activeClassName="active" to={ROUTES.ADMINFIELDS}>
                  <i className="tim-icons icon-notes mr-2"></i>
                  Fields
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink tag={RRNavLink} activeClassName="active" to={ROUTES.ADMINANALYTICS}>
                  <i className="tim-icons icon-chart-bar-32 mr-2"></i>
                  Analytics
                </NavLink>
              </NavItem>
              {isSuperAdmin && (
                <>
                  <NavItem>
                    <NavLink tag={RRNavLink} activeClassName="active" to={ROUTES.ADMIN + '/users'}>
                      <i className="tim-icons icon-single-02 mr-2"></i>
                      All Users
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink tag={RRNavLink} activeClassName="active" to={ROUTES.ADMINCHATBOT}>
                      <i className="tim-icons icon-chat-33 mr-2"></i>
                      Chatbot
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink tag={RRNavLink} activeClassName="active" to={ROUTES.ADMIN + '/cleanup'}>
                      <i className="tim-icons icon-trash-simple mr-2"></i>
                      Cleanup
                    </NavLink>
                  </NavItem>
                </>
              )}
              {/* Reporting is visible to any org admin (the outer page guard already denies non-admins). */}
              <NavItem>
                <NavLink tag={RRNavLink} activeClassName="active" to={ROUTES.ADMINREPORTING}>
                  <i className="tim-icons icon-paper mr-2"></i>
                  Reporting
                </NavLink>
              </NavItem>
            </Nav>
          </Col>

          <Col md="10" lg="10">
            <div className="card">
              <div className="card-body">
            {/* Render routes */}
            <Switch>
              <Route exact path={path} component={Members} />
              <Route exact path={`${path}/invites`} component={Invites} />
              <Route exact path={`${path}/fields`} component={Fields} />
              {isSuperAdmin && (
                <Route exact path={`${path}/users`} component={Users} />
              )}
              {isSuperAdmin && (
                <Route exact path={`${path}/chatbot`} component={ChatbotSettings} />
              )}
              {isSuperAdmin && (
                <Route exact path={`${path}/cleanup`} render={() => CleanupCard} />
              )}
              <Route exact path={`${path}/analytics`} component={Analytics} />
              <Route exact path={`${path}/reporting`} component={Reporting} />
              <Route exact path={`${path}/organization`} render={() => OrganizationCard} />
            </Switch>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

// Authorization is handled inside the component based on org role
const condition = (authUser) => !!authUser;

export default withAuthorization(condition)(AdminPage);
